// Pure helper functions extracted from `nearst-search.js` so they can be
// unit-tested without a browser environment. `nearst-search.js` re-exports
// them so browsers and Node tests both see the same implementations.

const DEFAULTS = require('./defaults.json');

function productUrl(slug, barcode) {
	const utm = new URLSearchParams({
		utm_source: 'nearst',
		utm_content: slug,
		utm_medium: 'search-widget',
		utm_campaign: 'search-widget'
	});
	return `https://${slug}.near.st/product/${encodeURIComponent(barcode)}?${utm}`;
}

function formatPrice(product) {
	if (typeof product.price !== 'number') return '';
	const currency = product.currency || 'GBP';
	try {
		return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(product.price);
	} catch {
		return `${currency} ${product.price}`;
	}
}

// Escape a value for safe interpolation into HTML text or attribute context.
// Retailer-controlled product data (titles, brands) reaches this widget from
// POS uploads, CSV, dashboard edits and shared enrichment — a poisoned row
// via the shared products table could otherwise ship attacker HTML to every
// shop selling that GTIN. Shadow DOM does NOT sanitise; inline event handlers
// on `<img>`/`<svg>` fire when set via innerHTML.
const HTML_ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeHtml(value) {
	return String(value ?? '').replace(/[&<>"']/g, (c) => HTML_ESC[c]);
}

// Restrict image URLs to https so a poisoned image field can't smuggle
// `javascript:` or `data:text/html;…` schemes into `<img src>` (Chrome
// blocks those in <img> today, but the belt-and-braces guard is cheap and
// deflects future spec relaxations).
function safeImageUrl(url) {
	return typeof url === 'string' && /^https:\/\//.test(url) ? url : '';
}

// Typesense stores product images as an `images` array of CDN URLs, and sets
// `no_images: true` when there are none. The widget originally asked for a
// field called `image_link`, which the collection has never had — so every
// result rendered the grey placeholder. Tolerates a bare string in case the
// field shape changes back.
function firstImageUrl(images) {
	if (Array.isArray(images)) return safeImageUrl(images.find((url) => safeImageUrl(url)));
	return safeImageUrl(images);
}

// A bare family name is a trap. `font-family: Poppins` is valid CSS, so when
// the font isn't loaded the browser falls back to its *initial* value — Times —
// and the widget looks broken rather than neutral. A trailing comma
// (`Poppins,`) is worse in the other direction: it's invalid, the whole
// declaration is dropped, and nothing appears to happen at all.
//
// Lives here rather than in the dashboard so the guarantee holds at every
// entry point. The install page normalises before it builds the snippet, but a
// retailer hand-editing the copied snippet to `font-family="Poppins"` reaches
// the widget directly — and that is a first-class path for a copy-paste embed.
function normaliseFontStack(value) {
	const trimmed = String(value ?? '')
		.trim()
		.replace(/,\s*$/, '');
	if (!trimmed || trimmed === DEFAULTS.fontFamily) return DEFAULTS.fontFamily;
	if (trimmed.includes(',')) return trimmed;
	const quoted = /\s/.test(trimmed) && !/^['"]/.test(trimmed) ? `"${trimmed}"` : trimmed;
	return `${quoted}, sans-serif`;
}

module.exports = { productUrl, formatPrice, escapeHtml, safeImageUrl, firstImageUrl, normaliseFontStack };
